import { type ComponentProps } from "react";
import { useNavigate, useParams } from "react-router";
import { Form, Formik, type FormikErrors } from "formik";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { CheckboxField } from "@vega-core/components/Form/Fields/CheckboxField";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { toEditorHtml } from "@vega-core/components/MinimalRichTextEditor/lexicalHtml";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { htmlToPlainText } from "@vega-core/utils/richText";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { FormGroup } from "../components/CoInvestmentEditForm/components/FormGroup";
import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { DraftEmailEditor } from "../components/DraftEmail/DraftEmailEditor";
import { useCoInvestDealReadOnly } from "../hooks/useCoInvestDealReadOnly";
import { useDealEmailTemplate } from "../hooks/useDealEmailTemplate";
import { useDealName } from "../hooks/useDealName";

import { ReportSectionMessage } from "./components/ReportSectionMessage";

type EmailTemplateFormValues = {
  subject: string;
  body: string;
  attachNda: boolean;
  attachTeaser: boolean;
};

const validate = (values: EmailTemplateFormValues) => {
  const errors: FormikErrors<EmailTemplateFormValues> = {};

  if (!values.subject.trim()) {
    errors.subject = m.coInvestments_editEmail_validation_subjectRequired();
  }

  if (!htmlToPlainText(values.body)) {
    errors.body = m.coInvestments_editEmail_validation_required();
  }

  return errors;
};

export const EmailTemplatePage = () => {
  const { id: dealIdParams } = useParams<{ id: string }>();
  const dealId = dealIdParams ?? "";
  const navigate = useNavigate();

  useFeatureFlagGuard(
    "gp_co_investment",
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const { isMutationLocked } = useCoInvestDealReadOnly();
  const isEditable = !isMutationLocked;

  const { template, isLoading, isError, refetch, save } =
    useDealEmailTemplate(dealId);

  const headerProps: ComponentProps<typeof CoInvestmentPageHeader> = {
    backButtonDataTestId: "back-to-configure-button",
    capitalize: false,
    fundName: dealName,
    isLoading: isDealNameLoading,
    onBack: () =>
      navigate(
        CoInvestmentFullPaths.CoInvestmentConfigure.replace(":id", dealId),
      ),
    showClosedDealNotice: true,
    showDrawer: false,
    title: m.coInvestments_nav_emailTemplate(),
  };

  if (isError || isLoading || !template) {
    return (
      <div
        className="my-4 max-w-3xl mx-auto px-4"
        data-testid="co-investments-email-template"
      >
        <CoInvestmentPageHeader {...headerProps} />
        <div className="mt-8">
          {isError ? (
            <ReportSectionMessage
              dataTestId="email-template-error"
              message={m.common_error_somethingWentWrong()}
              onRetry={refetch}
            />
          ) : (
            <Skeleton className="h-80 w-full" />
          )}
        </div>
      </div>
    );
  }

  const initialValues: EmailTemplateFormValues = {
    subject: template.subject,
    body: toEditorHtml(template.body),
    attachNda: template.attachNda,
    attachTeaser: template.attachTeaser,
  };

  return (
    <Formik<EmailTemplateFormValues>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => save({ ...values, subject: values.subject.trim() })}
      validate={validate}
    >
      {({ values, errors, dirty, isSubmitting, setFieldValue }) => (
        <Form
          className="my-4 max-w-3xl mx-auto px-4"
          data-testid="co-investments-email-template"
        >
          <CoInvestmentPageHeader
            {...headerProps}
            action={
              isEditable ? (
                <ThemedButton
                  dataTestId="email-template-save"
                  disabled={!dirty || isSubmitting}
                  isLoading={isSubmitting}
                  type="submit"
                  variant="primary"
                >
                  {m.common_save()}
                </ThemedButton>
              ) : undefined
            }
          />

          <div className="mt-8">
            <FormGroup
              dataTestId="email-template"
              label={m.coInvestments_emailTemplate_outreachTitle()}
            >
              <div className="space-y-4">
                <p className="body-base-regular text-text/secondary">
                  {m.coInvestments_emailTemplate_description()}
                </p>
                <InputField
                  disabled={!isEditable}
                  fullWidth
                  name="subject"
                  placeholder={m.coInvestments_editEmail_subjectPlaceholder()}
                  withBorder
                />
                <div className="flex flex-col gap-1">
                  <DraftEmailEditor
                    dataTestId="email-template-body"
                    hasError={!!errors.body}
                    onChange={(html) => setFieldValue("body", html)}
                    placeholder={m.coInvestments_editEmail_placeholder()}
                    placeholderVariables={template.placeholderVariables}
                    readOnly={!isEditable}
                    value={values.body}
                  />
                  {errors.body ? (
                    <ValidationErrorText dataTestId="email-template-body-error">
                      {errors.body}
                    </ValidationErrorText>
                  ) : null}
                </div>
                <CheckboxField
                  disabled={!isEditable}
                  label={m.coInvestments_emailTemplate_attachNda()}
                  name="attachNda"
                />
                <CheckboxField
                  disabled={!isEditable}
                  label={m.coInvestments_emailTemplate_attachTeaser()}
                  name="attachTeaser"
                />
              </div>
            </FormGroup>
          </div>
        </Form>
      )}
    </Formik>
  );
};
